import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { type AssetClass } from "@vega-api/generated/types/assetClass";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { type CollaboratorPermissionEntry } from "@vega-api/generated/types/collaboratorPermissionEntry";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { convertToTitleCase } from "@vega-core/utils/formatString";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { FundLogos } from "@vega-modules/components/AssetClassLogos/AssetClassImages";

import { deduplicatePermissionsByRoleId } from "../components/collaboratorsV2Constants";

import { useCollaboratorRoles } from "./useCollaboratorRoles";

export function groupCollabPermissionsByRole(
  permissions: CollaboratorPermissionEntry[],
): Record<string, CollaboratorPermissionEntry[]> {
  return permissions.reduce(
    (acc, permission) => {
      const { roleId } = permission;

      if (isFalsy(roleId)) {
        return acc;
      }

      if (isFalsy(acc[roleId])) {
        acc[roleId] = [];
      }

      acc[roleId].push(permission);

      return acc;
    },
    {} as Record<string, CollaboratorPermissionEntry[]>,
  );
}

export const useCollaboratorsRolesTableConfig = ({
  selectedCollaborator,
}: {
  selectedCollaborator: CollaboratorDetails;
}) => {
  const { getRoleById, isLoading: isLoadingRoles } = useCollaboratorRoles();

  const permissionsByRole = deduplicatePermissionsByRoleId(
    selectedCollaborator.permissions ?? [],
  );

  const checkSubjectName = (subjectName: string | undefined) => {
    if (subjectName && subjectName !== "UNDEFINED_SUBJECT") {
      return subjectName;
    } else {
      return m.common_allFunds();
    }
  };

  const permissionsDataColumns: ColumnDef<CollaboratorPermissionEntry>[] =
    useMemo(
      () => [
        {
          header: m.common_subject(),
          accessorKey: "subject",
          cell: ({ row }) => {
            const { icon = "", subjectName } = row.original;

            return (
              <div className="flex items-center gap-3">
                <FundLogos assetClass={icon as AssetClass} />
                <div>{checkSubjectName(subjectName)}</div>
              </div>
            );
          },
        },
        {
          header: m.common_role(),
          accessorKey: "role",
          cell: ({ row }) => {
            const role = getRoleById(row.original.roleId as string);

            return isTruthy(role) && isTruthy(role.name) ? (
              <Tag variant={TagVariants.Regular}>{role.name.toLowerCase()}</Tag>
            ) : (
              "-"
            );
          },
        },
      ],

      [getRoleById],
    );

  const getUserPermissionsByRoleDataColumns = (
    roleId: string,
  ): ColumnDef<CollaboratorPermissionEntry>[] => [
    {
      header: () => {
        const role = getRoleById(roleId);

        return isTruthy(roleId) && isTruthy(role) && isTruthy(role.name)
          ? convertToTitleCase(role.name)
          : m.common_role();
      },
      accessorKey: "subject",
      cell: ({ row }) => {
        const { icon = "", subjectName } = row.original;

        return (
          <div className="flex items-center gap-3">
            <FundLogos assetClass={icon as AssetClass} />
            <div className="capitalize">{checkSubjectName(subjectName)}</div>
          </div>
        );
      },
    },
  ];

  const userPermissionsByRoleData =
    groupCollabPermissionsByRole(permissionsByRole);

  return {
    collaboratorHasNoPermissions: permissionsByRole.length === 0,
    getUserPermissionsByRoleDataColumns,
    permissionsDataColumns,
    userPermissionsByRoleData: userPermissionsByRoleData,
    userPermissionsData: permissionsByRole,
    isRolesLoading: isLoadingRoles,
  };
};
